import { t as uiText } from '../i18n';
// src/components/ArchivedBoardList.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuthContext } from '../context/AuthContext';
import { getArchivedBoards } from '../features/search/searchUtils';
import { PreviewData } from '../features/collaboration/searchDataUtils';
import { getBoardHashtags } from '../features/hashtag/hashtagUtils';
import NotePreviewCard from './NotePreviewCard';
import { workspacePagePath } from '../utils/workspaceNavigation';



interface BoardData {
  id: string;
  title: string;
  content: string;
  firstImageUrl?: string | null;
  lastModified: any;
  lastModifiedBy: string;
  version: number;
  slug: string;
  archived?: boolean;
  // 新しいDB構造フィールド
  previewData?: PreviewData;
  hashtags?: string[]; // タグ情報を追加
  isCrawled?: boolean; // クローラーで取得されたページかどうか
}

interface ArchivedBoardListProps {
  isSelectionMode?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  selectedBoardIds?: string[];
  refreshTrigger?: number;
  sortOrder?: 'asc' | 'desc';
  sortBy?: 'lastModified' | 'createdAt' | 'title' | 'contentSize';
}



const ArchivedBoardList: React.FC<ArchivedBoardListProps> = ({
  isSelectionMode = false,
  onSelectionChange,
  selectedBoardIds = [],
  refreshTrigger = 0,
  sortOrder = 'desc',
  sortBy = 'lastModified',
}) => {
  const navigate = useNavigate();
  const { workspaceId: routeWorkspaceId } = useParams();
  const { user } = useAuthContext();
  const [boards, setBoards] = useState<BoardData[]>([]);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  // レスポンシブ対応用の状態
  const [isMobile, setIsMobile] = useState(false);

  // レスポンシブ対応のウィンドウサイズ監視
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 初期チェック
    checkIsMobile();
    
    // リサイズイベントリスナーを追加
    window.addEventListener('resize', checkIsMobile);
    
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // アーカイブされたボード一覧を取得
  useEffect(() => {
    const fetchArchivedBoards = async () => {
      if (!routeWorkspaceId || !user) {
        setLoading(false);
        return;
      }

      // 既にローディング中の場合は重複実行を防ぐ
      if (loadingRef.current) {
        return;
      }

      loadingRef.current = true;
      setLoading(true);

      try {
        const boardsData = await getArchivedBoards(routeWorkspaceId);

        // 各ボードのタグ情報を取得
        const boardsWithHashtags = await Promise.all(
          boardsData.map(async (board) => {
            try {
              const hashtags = await getBoardHashtags(routeWorkspaceId, board.id);
              return { ...board, hashtags };
            } catch (error) {
              console.warn(`⚠️ アーカイブボード ${board.id} のタグ取得エラー:`, error);
              return { ...board, hashtags: [] };
            }
          })
        );

        // sortBy に応じてソート
        const getLastModifiedTime = (board: BoardData): number => {
          const lm = board.lastModified;
          if (!lm?.toDate) return 0;
          return (lm as any).toDate().getTime();
        };
        const getCreatedAtTime = (board: BoardData): number => {
          const ca = (board as any).createdAt;
          if (!ca?.toDate) return 0;
          return (ca as any).toDate().getTime();
        };
        const getTitleSortKey = (board: BoardData): string =>
          (board.title ?? '').trim().toLowerCase();
        const getContentSize = (board: BoardData): number =>
          (board as any).searchData?.originalContentTextCount ?? (board as any).previewData?.structuredData?.wordCount ?? 0;

        boardsWithHashtags.sort((a, b) => {
          let diff = 0;
          switch (sortBy) {
            case 'lastModified':
              diff = getLastModifiedTime(b) - getLastModifiedTime(a);
              break;
            case 'createdAt':
              diff = getCreatedAtTime(b) - getCreatedAtTime(a);
              break;
            case 'title':
              diff = getTitleSortKey(a).localeCompare(getTitleSortKey(b), 'ja');
              break;
            case 'contentSize':
              diff = getContentSize(b) - getContentSize(a);
              break;
            default:
              diff = getLastModifiedTime(b) - getLastModifiedTime(a);
          }
          return sortOrder === 'asc' ? (sortBy === 'title' ? diff : -diff) : (sortBy === 'title' ? -diff : diff);
        });

        setBoards(boardsWithHashtags);
      } catch (error) {
        console.error('  - アーカイブボード一覧の取得エラー:', error);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    fetchArchivedBoards();
  }, [routeWorkspaceId, user, refreshTrigger, sortOrder, sortBy]);

  const handleBoardClick = (slug: string) => {
    if (routeWorkspaceId) {
      const url = workspacePagePath(routeWorkspaceId, slug);
      navigate(url);
    } else {}
  };

  const handleBoardSelect = (boardId: string) => {
    if (!onSelectionChange) return;
    
    const newSelectedIds = selectedBoardIds.includes(boardId)
      ? selectedBoardIds.filter(id => id !== boardId)
      : [...selectedBoardIds, boardId];
    
    onSelectionChange(newSelectedIds);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <div>{uiText("Loading now...")}</div>
      </div>
    );
  }

  if (boards.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px',
        color: '#666',
        fontSize: '16px'
      }}>
        {uiText("No archived boards")}
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? '8px' : '16px',
        width: '100%',
        margin: '0'
      }}>
        {/* アーカイブされたボードカード */}
        {boards.map((board) => (
          <div
            key={board.id}
            style={{
              position: 'relative',
              opacity: 0.8, // アーカイブされたボードは少し薄く表示
            }}
          >
            {/* アーカイブバッジ */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(108, 117, 125, 0.7)',
                color: 'white',
                fontSize: '10px',
                padding: '2px 6px',
                borderRadius: '4px',
                zIndex: 2,
              }}
            >
              {uiText("Archive")}
            </div>

            <NotePreviewCard
              id={board.id}
              title={board.title}
              content={board.content}
              firstImageUrl={board.firstImageUrl}
              lastModified={board.lastModified}
              slug={board.slug}
              archived={board.archived}
              isSelectionMode={isSelectionMode}
              isSelected={selectedBoardIds.includes(board.id)}
              isMobile={isMobile}
              onCardClick={handleBoardClick}
              onSelectionChange={handleBoardSelect}
              showPinButton={false}
              hashtags={board.hashtags}
              isCrawled={board.isCrawled}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ArchivedBoardList; 